import {
    createContext,
    useState,
    useEffect,
    useCallback,
    ReactNode,
    useContext,
    useRef,
} from 'react';
import { EventType } from '@azure/msal-browser';
import { fetchMyProfile } from '../apis/UserProfileService';
import type { UserDetails } from '../infrastructure/models';
import { useAuth } from './AuthContext';
import msalInstance from '../msal';
import { handleSessionExpiry } from '../utils/sessionGuard';

interface UserProfileContextShape {
    /** The currently loaded user profile, or null while loading / on error. */
    profile: UserDetails | null;
    /** Whether the profile is currently being fetched. */
    loading: boolean;
    /** Force a refetch of the profile (e.g. after a profile update). */
    refetch: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextShape | undefined>(undefined);

export const UserProfileProvider = ({ children }: { children: ReactNode }) => {
    const { user, logout } = useAuth();
    const [profile, setProfile] = useState<UserDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [msalLoggedIn, setMsalLoggedIn] = useState(() => msalInstance.getAllAccounts().length > 0);
    /** Guard to avoid concurrent fetches (e.g. double-render in StrictMode). */
    const fetchingRef = useRef(false);
    /** Prevents further profile fetches after a failure-triggered logout. */
    const loggedOutRef = useRef(false);

    useEffect(() => {
        const callbackId = msalInstance.addEventCallback((event) => {
            if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
                setMsalLoggedIn(true);
            }
            if (event.eventType === EventType.LOGOUT_SUCCESS) {
                setMsalLoggedIn(false);
            }
        });

        return () => {
            if (callbackId) {
                msalInstance.removeEventCallback(callbackId);
            }
        };
    }, []);

    const loadProfile = useCallback(async () => {
        // Prevent concurrent fetches or retries after logout
        if (fetchingRef.current || loggedOutRef.current) return;
        fetchingRef.current = true;
        setLoading(true);

        try {
            const data = await fetchMyProfile();
            setProfile(data);
        } catch {
            // API call failed — mark as logged-out to break any retry loop,
            // clear msalLoggedIn so isLoggedIn becomes false, then force logout.
            loggedOutRef.current = true;
            setMsalLoggedIn(false);
            await handleSessionExpiry(msalInstance);
            await logout();
            window.location.href = '/login';
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [logout]);

    // Fetch profile automatically when the user is logged in
    const isLoggedIn = user.loggedIn || msalLoggedIn;

    useEffect(() => {
        if (isLoggedIn) {
            // Reset the logged-out guard when a new login is detected
            loggedOutRef.current = false;
            loadProfile();
        } else {
            // Clear profile when the user logs out
            setProfile(null);
        }
    }, [isLoggedIn, loadProfile]);

    // If the user is logged in but profile is missing (e.g. context consumed
    // before the initial fetch finished, or state was cleared), trigger a
    // refetch automatically.
    useEffect(() => {
        if (isLoggedIn && !profile && !loading) {
            loadProfile();
        }
    }, [isLoggedIn, profile, loading, loadProfile]);

    const refetch = useCallback(async () => {
        await loadProfile();
    }, [loadProfile]);

    return (
        <UserProfileContext.Provider value={{ profile, loading, refetch }}>
            {children}
        </UserProfileContext.Provider>
    );
};

/**
 * Hook to access the current user profile.
 * Must be used within a `<UserProfileProvider>`.
 */
export const useUserProfile = (): UserProfileContextShape => {
    const ctx = useContext(UserProfileContext);
    if (!ctx) {
        throw new Error('useUserProfile must be used within UserProfileProvider');
    }
    return ctx;
};

export { UserProfileContext };
